import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FileDropzone } from '@/components/upload/FileDropzone'
import { WorkflowSelector, Workflow } from '@/components/upload/WorkflowSelector'
import { UploadProgress } from '@/components/upload/UploadProgress'
import { useUpload } from '@/hooks/use-upload'

export function Upload() {
  const navigate = useNavigate()
  const { upload, progress, status, error, reset, isUploading } = useUpload()

  // Local state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [workflow, setWorkflow] = useState<Workflow>('router')

  /**
   * Handle file selection from dropzone.
   */
  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file)
    reset() // Clear any previous upload state
  }, [reset])

  /**
   * Handle upload button click.
   */
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return

    try {
      const result = await upload({
        file: selectedFile,
        workflow,
      })

      // Redirect to run detail page on success
      // Use fileId from result, or uploadId as fallback
      const targetId = result.fileId || result.uploadId
      navigate(`/runs/${targetId}`)
    } catch (err) {
      // Error is handled by useUpload hook
      console.error('Upload failed:', err)
    }
  }, [selectedFile, workflow, upload, navigate])

  /**
   * Handle retry after error.
   */
  const handleRetry = useCallback(() => {
    reset()
    if (selectedFile) {
      handleUpload()
    }
  }, [reset, selectedFile, handleUpload])

  /**
   * Clear selected file and start over.
   */
  const handleClear = useCallback(() => {
    setSelectedFile(null)
    reset()
  }, [reset])

  // Determine if upload is in progress or complete
  const showProgress = status !== 'idle' && selectedFile
  const canUpload = selectedFile && !isUploading && status !== 'complete'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Upload Files</h1>
        <p className="text-muted-foreground">
          Upload Excel or PDF files for rate extraction processing.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left column: File selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select File</CardTitle>
            <CardDescription>
              Drag and drop a file or click to browse
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showProgress ? (
              <FileDropzone
                onFileSelect={handleFileSelect}
                disabled={isUploading}
              />
            ) : (
              <div className="space-y-4">
                <UploadProgress
                  fileName={selectedFile.name}
                  status={status}
                  progress={progress}
                  error={error}
                  onRetry={status === 'error' ? handleRetry : undefined}
                />
                {status === 'error' && (
                  <Button variant="outline" onClick={handleClear}>
                    Select Different File
                  </Button>
                )}
              </div>
            )}

            {/* Selected file info (when not uploading) */}
            {selectedFile && status === 'idle' && (
              <div className="mt-4 flex items-center justify-between rounded-lg border p-3 bg-muted/50">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate max-w-[200px]">
                    {selectedFile.name}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleClear}>
                  Remove
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right column: Workflow selection */}
        <Card>
          <CardHeader>
            <CardTitle>Configure Processing</CardTitle>
            <CardDescription>
              Choose how the file should be processed
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <WorkflowSelector
              value={workflow}
              onChange={setWorkflow}
              disabled={isUploading}
            />

            {/* Upload button */}
            <Button
              onClick={handleUpload}
              disabled={!canUpload}
              className="w-full"
              size="lg"
            >
              {isUploading ? (
                <>
                  <span className="animate-spin mr-2">...</span>
                  Uploading...
                </>
              ) : (
                'Start Processing'
              )}
            </Button>

            {/* Help text */}
            <p className="text-xs text-muted-foreground text-center">
              After upload, you'll be redirected to view processing progress.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
